import { getTaskStatusCode, TaskStatus } from "./../../models/task-status";
import { getTaskStatus } from "@/models/task-status";
import { client } from "./../../api/request";

export interface GetServiceTasksResponse {
  id: number;
  name: string;
  // 最新の編集者
  staff: {
    name: string;
  };
  // 最終更新日時 DateTime
  updateDate: string;
  projectLinksVisible: boolean;
  days: {
    date: string; // Date
    tasks: ServiceTask[];
  }[];
}

/**
 * 工程
 */
export interface ServiceTask {
  id: number;
  name: string;
  status: TaskStatus;
  pending: boolean;
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd
  project: {
    id: number;
    name: string;
    staff: {
      id: number;
      name: string;
    } | null;
  };
  client: {
    name: string;
    staff: {
      id: number;
      name: string;
    };
  } | null;
  memo: string;
  memoEmphasized: boolean;
  otherTasks: {
    id: number;
    shortName: string;
    date: string; // yyyy-MM-dd
  }[];
  constructionTasks: {
    id: number;
    shortName: string;
    status: "done" | "pending";
    alignment: string; // left | center | right
  }[];
}

export interface GetServiceTasksQuery {
  serviceTaskTypeId: number;
  date: string; // yyyy-MM-dd, 施工日を与えると1週間分返ってくる
  companyId?: number; // 発注者
  officeId?: number;
  staffId?: number; // 施工者
  constructionTaskId?: number;
}

/**
 * セルビス工程表を取得する
 */
export async function getServiceTasks(
  query: GetServiceTasksQuery
): Promise<GetServiceTasksResponse | null> {
  const response = await client.get("get_service_tasks.php", query);
  if (!response.length) {
    return null;
  }
  return {
    ...response[0],
    days: response[0].days.map((day: any) => ({
      ...day,
      tasks: day.tasks.map(Task)
    }))
  };
}

/**
 * 工程を取得する
 */
export async function getServiceTask(id: number, serviceTaskTypeId:number): Promise<ServiceTask | null> {
  const response = await client.get("get_service_task_detail.php", {
    id,
    serviceTaskTypeId
  });
  return response.length > 0 ? Task(response[0]) : null;
}

export interface ServiceTaskFormRequest {
  id?: number;
  pending: boolean;
  startDate: string;
  endDate: string;
  staff: string;
  memo: string;
  memoEmphasized: boolean;
  otherTasks: {
    id: number;
    shortName: string;
    date: string; // yyyy-MM-dd
  }[];
  constructionTasks: {
    id: number;
    shortName: string;
    status: "done" | "pending";
  }[];
}

/**
 * 工程を変更する
 */
export async function saveServiceTask(
  data: ServiceTaskFormRequest
): Promise<void> {
  const d = new FormData();
  if (data.id) {
    d.append("id", String(data.id));
  }
  d.append("pending", data.pending ? "1" : "0");
  d.append("startDate", data.startDate);
  d.append("endDate", data.endDate);
  d.append("constructionStaff", data.staff);
  d.append("memo", data.memo);
  d.append("memoEmphasized", data.memoEmphasized ? "1" : "0");
  data.otherTasks.forEach((t, i) => {
    d.append("otherTasks[" + i + "][id]", String(t.id));
    d.append("otherTasks[" + i + "][date]", t.date);
  });
  data.constructionTasks.forEach((t, i) => {
    d.append("constructionTasks[" + i + "][id]", String(t.id));
    d.append("constructionTasks[" + i + "][status]", t.status);
  });

  await client.post("save_service_task.php", d);
}

/**
 * セルビス工程表の種類
 */
export interface ServiceTaskType {
  id: string;
  name: string;
  frozen: boolean;
}

export interface SaveServiceTaskTypeCommand {
  id?: string;
  name: string;
}

/**
 * セルビス工程表の種類を取得する
 */
export async function getServiceTaskTypes(): Promise<ServiceTaskType[]> {
  return await client.get("get_service_task_types.php");
}

export interface SearchServiceTaskQuery {
  serviceTaskTypeId: number;
  keyword?: string;
  status?: TaskStatus | "";
}

/**
 * セルビス工程表の種類を新規作成/更新する (idが空なら新規作成)
 */
export async function saveServiceTaskType(
  newTaskType: SaveServiceTaskTypeCommand
): Promise<void> {
  const data = new FormData();
  if (newTaskType.id) {
    data.append("id", newTaskType.id);
  }
  data.append("name", newTaskType.name);
  await client.post("save_service_task_type.php", data);
}

/**
 * idからセルビス工程表の種類を削除する
 */
export async function deleteServiceTaskType(
  serviceTaskId: string
): Promise<void> {
  const data = new FormData();
  data.append("id", serviceTaskId);
  await client.post("delete_service_task_type.php", data);
}

/**
 * idからセルビス工程表の種類を単体で取得する
 */
export async function getServiceTaskType(id: string): Promise<ServiceTaskType> {
  const response = await client.get("get_service_task_type.php", {
    id
  });
  return response;
}

/**
 * 工程を検索する
 */
export async function searchServiceTask(
  query: SearchServiceTaskQuery
): Promise<ServiceTask[]> {
  const response = await client.get("get_service_schedule_tasks.php", {
    ...query,
    status: query.status && getTaskStatusCode(query.status)
  });
  return response.tasks.map(Task);
}

function Task(data: any): ServiceTask {
  return {
    ...data,
    status: getTaskStatus(data.status),

    // TODO: APIが文字列で返ってくるのでbooleanに直す
    pending: Boolean(data.pending),

    // TODO: APIのstaffが配列で返ってくるので単体に直す
    project: {
      ...data.project,
      staff: data.project.staff.length > 0 ? data.project.staff[0] : null
    }
  };
}
